import template from 'url-template';

import axios from 'axios';
import { get, orderBy } from 'lodash';

import {
    FETCH_ALL_COMPANIES,
    FETCH_APPLICATIONS,
    FETCH_COMPANY_METRICS
} from './actionTypes';

import { dataUrlToBlob } from '../utils/blob-util';

import awsHeader from '../helpers/awsAuth';
import k2aBaseApi from '../apis/key2act';
import lambdaBaseApi from '../apis/lambda';

import { handleNetworkError, enqueueSnackbar } from './applicationMessageActions';
import { setBobLoading } from './bobActions';

const UpdateCompanyUrl = template.parse('/companies/{companyId}');
const CompaniesUrl = template.parse('/companies?page=1&limit=1000');
const CreateCompanyApplicationUrl = template.parse('/companies/{companyId}/applications/{applicationId}');
const CompanyApplicationUrl = template.parse('/companies/{companyId}/applications');
const CompanyMetricsUrl = template.parse('/companies/{companyId}/metrics');
const UpdateCompanyApplicationUrl = template.parse('/companies/{companyId}/applications/{companyApplicationId}');
const ImportUrl = template.parse('/storage/companyLogo');
const ApplicationsUrl = template.parse('/applications');
const DisableCompanyUrl = template.parse('/companies/disable');
const EnableCompanyUrl = template.parse('/companies/enable');

const doFileImport = (file, header) => async (dispatch, getState) =>{
    try {
        const fileImportResponse = await lambdaBaseApi.put(
            ImportUrl.expand(),
            {},
            header
        );

        const blob = await dataUrlToBlob(file);

        await axios({
            method:  'put',
            url:     fileImportResponse.data.url,
            data:    blob,
            headers: {
                'Content-Type': 'image/png'
            }
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const createCompany = (body) => async (dispatch, getState) => {
    const header = await awsHeader();

    let postPayload;
    try {
        try {
            const postResponse = await k2aBaseApi.post(
                CompaniesUrl.expand(),
                body,
                header
            );
            postPayload = get(postResponse, 'data.id', '');

            dispatch(enqueueSnackbar('Company created successfully.', 'success'));
        } catch (error) {
            dispatch(enqueueSnackbar('Network Error - Company not created.', 'error'));

            dispatch(handleNetworkError(error));
        }

        try {
            if (body.fileImport) {
                // Creating a new instance of the headers because we are manipulating the K2A-Company
                // property to reflect the newly created company. This is non-standard and should
                // only be modified with care.
                const fileImportHeader = await awsHeader();
                fileImportHeader.headers['K2A-Company'] = postPayload;

                await doFileImport(body.fileImport, fileImportHeader);

                // Giving the server 5 seconds to process file upload before GET'ting the companies
                await _timeout(5000);
            }
        } catch (error) {
            dispatch(enqueueSnackbar('Network Error - Company Logo not uploaded.', 'error'));

            dispatch(handleNetworkError(error));
        }

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const updateCompany = (companyId, body, options = {}) => async dispatch => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.put(
            UpdateCompanyUrl.expand({
                companyId
            }),
            body,
            header
        );

        if (body.fileImport) await doFileImport(body.fileImport, header);

        dispatch(enqueueSnackbar('Company updated successfully.', 'success'));

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));

        return false;
    }
};


export const fetchApplications = () => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('FetchCompanyApplications'));
    try {
        const response = await k2aBaseApi.get(
            ApplicationsUrl.expand(),
            header
        );

        const payload = get(response, 'data', []);

        dispatch({
            type: FETCH_APPLICATIONS,
            payload
        });
        dispatch(setBobLoading('FetchCompanyApplications'));
    } catch (error) {
        dispatch(setBobLoading('FetchCompanyApplications'));
        dispatch(handleNetworkError(error));
    }
};

export const fetchApplicationsTiles = () => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('FetchCompanyApplications'));
    try {
        const response = await k2aBaseApi.get(
            ApplicationsUrl.expand(),
            header
        );

        const payload = get(response, 'data', []);

        dispatch({
            type: FETCH_APPLICATIONS,
            payload
        });
        dispatch(setBobLoading('FetchCompanyApplications'));
        return payload;
    } catch (error) {
        dispatch(setBobLoading('FetchCompanyApplications'));
        dispatch(handleNetworkError(error));
    }
};

export const fetchCompanies = () => async (dispatch, getState) => {
    const header = await awsHeader();
    dispatch(setBobLoading('companyManagement'));

    try {
        const response = await k2aBaseApi.get(
            CompaniesUrl.expand(),
            header
        );
        
        let companies = get(response.data, 'data', []);
        const meta = get(response.data, 'meta', {});

        companies = orderBy(companies, [c => c.name.toLowerCase()]);

        companies.forEach(c => {
            const sortedApps = orderBy(c.companyApplications, [app => app.name.toLowerCase()]);
            c.applicationList = sortedApps.map(a => a.name).join(', ');
        });

        dispatch({
            type:    FETCH_ALL_COMPANIES,
            payload: {
                companies,
                meta
            }
        });

    } catch (error) {
        dispatch(handleNetworkError(error));
    } finally {
        dispatch(setBobLoading('companyManagement'));
    }
};

export const DisableCompany = (companyIds) => async dispatch => {
    const header = await awsHeader();

    try {
        let formBody = {
            'companyIds': companyIds
        };

        await k2aBaseApi.post(
            DisableCompanyUrl.expand(),
            formBody,
            header
        );

        await Promise.all([
            dispatch(enqueueSnackbar('Company disabled.', 'success')),
            dispatch(fetchCompanies())
        ]);
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const EnableCompany = (companyIds) => async dispatch => {
    const header = await awsHeader();

    try {
        let formBody = {
            'companyIds': companyIds
        };

        await k2aBaseApi.post(
            EnableCompanyUrl.expand(),
            formBody,
            header
        );

        await Promise.all([
            dispatch(enqueueSnackbar('Company enabled.', 'success')),
            dispatch(fetchCompanies())
        ]);
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

export const fetchCompanyApplications = companyId => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('FetchCompanyApplications'));
    try {
        const response = await k2aBaseApi.get(
            CompanyApplicationUrl.expand({ companyId }),
            header
        );

        const payload = get(response, 'data', []);

        dispatch(setBobLoading('FetchCompanyApplications'));
        return payload;
    } catch (error) {
        dispatch(setBobLoading('FetchCompanyApplications'));
        dispatch(handleNetworkError(error));
        return;
    }
};


export const createCompanyApplication = (companyId, applicationId) => async (dispatch, getState) => {
    const header = await awsHeader();


    const body = {
        activeState: 0
    };

    dispatch(setBobLoading('CompanyApplicationUpdate'));
    try {
        await k2aBaseApi.post(
            CreateCompanyApplicationUrl.expand({
                companyId,
                applicationId
            }),
            body,
            header
        );

        dispatch(setBobLoading('CompanyApplicationUpdate'));
        dispatch(enqueueSnackbar('Application added successfully.', 'success'));
        return true;
    } catch (error) {
        dispatch(setBobLoading('CompanyApplicationUpdate'));
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const deleteCompanyApplication = (companyId, companyApplicationId) => async (dispatch, getState) => {
    const header = await awsHeader();

    dispatch(setBobLoading('CompanyApplicationUpdate'));
    try {
        await k2aBaseApi.delete(
            UpdateCompanyApplicationUrl.expand({
                companyId,
                companyApplicationId
            }),
            header
        );

        dispatch(setBobLoading('CompanyApplicationUpdate'));
        dispatch(enqueueSnackbar('Application removed successfully.', 'success'));
        return true;
    } catch (error) {
        dispatch(setBobLoading('CompanyApplicationUpdate'));
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const updateCompanyApplications = (companyId, companyApplicationId) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        await k2aBaseApi.put(
            UpdateCompanyApplicationUrl.expand({
                companyId,
                companyApplicationId
            }),
            header
        );

        return true;
    } catch (error) {
        dispatch(handleNetworkError(error));
        return false;
    }
};

export const fetchCompanyMetrics = (companyId, options = {}) => async (dispatch, getState) => {
    const header = await awsHeader();

    try {
        const response = await k2aBaseApi.get(
            CompanyMetricsUrl.expand({
                companyId
            }),
            header
        );

        const payload = get(response, 'data', []);

        dispatch({
            type: FETCH_COMPANY_METRICS,
            payload
        });
    } catch (error) {
        dispatch(handleNetworkError(error));
    }
};

const _timeout = ms => {
    return new Promise(resolve => setTimeout(resolve, ms));
};
